import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertTitle, AlertDescription } from '@agion/ui'
import { usePermissions } from '@/shared/auth/permission-context'
import { settingsService, CostNotificationSettings, CostSummary } from '@/services/settings.service'
import { formatCurrency, getCurrencyPreference } from '@/shared/lib/currency'

const POLL_INTERVAL_MS = 60 * 60 * 1000 // 60 minutes

// Dismissal durations per breach index
const DISMISS_DURATIONS: Record<number, number> = {
  0: 24 * 60 * 60 * 1000, // 24h for info
  1: 8 * 60 * 60 * 1000,  // 8h for warning
  // index 2 (critical) is not dismissible
}

function getDismissKey(orgId: string, index: number): string {
  return `agion-budget-alert-dismissed-${orgId}-${index}`
}

function isDismissed(orgId: string, index: number): boolean {
  if (index >= 2) return false // critical and the budget-zero stop are never dismissible
  try {
    const stored = localStorage.getItem(getDismissKey(orgId, index))
    if (!stored) return false
    const expiry = parseInt(stored, 10)
    if (Date.now() < expiry) return true
    localStorage.removeItem(getDismissKey(orgId, index))
  } catch {
    // localStorage unavailable
  }
  return false
}

function dismissAlert(orgId: string, index: number): void {
  const duration = DISMISS_DURATIONS[index]
  if (duration == null) return
  try {
    localStorage.setItem(getDismissKey(orgId, index), String(Date.now() + duration))
  } catch {
    // localStorage unavailable
  }
}

type AlertLevel = 'info' | 'warning' | 'error'

/** The banner state for a budget of 0 that stops new runs; above the threshold indexes. */
const BUDGET_ZERO_INDEX = 3

interface BannerState {
  level: AlertLevel
  breachIndex: number
  usagePercent: number
  spent: string
  budget: string
  remaining: string
}

export const BudgetAlertBanner: React.FC = () => {
  const { t } = useTranslation(['pages', 'common'])
  const { isOwner, isAdmin, isManager, isAuditor, canNavigateTo, isLoading } = usePermissions()
  // governance serves the cost reads only to a role whose navigation allows
  // the Cost page; anyone else would get a 403 on every page this layout
  // renders (agion/Agion#2610). The layout also mounts while a next-variant
  // session resolves, so this guards the next interface's first page too.
  const mayReadCost = !isLoading && canNavigateTo('settings')
  const [banner, setBanner] = useState<BannerState | null>(null)
  const [orgId, setOrgId] = useState<string>('')
  const [dismissed, setDismissed] = useState(false)

  const computeBanner = useCallback((
    settings: CostNotificationSettings,
    summary: CostSummary
  ): BannerState | null => {
    // Don't show alerts for unconfigured orgs — these are server defaults only.
    // The backend checker also skips unconfigured orgs, so alerts would be misleading.
    if (!settings.configured) return null
    // The server's verdict that a saved budget of 0 stops new mission runs
    // (challenge 2026-10-12-budget-zero-no-spend): shown to every role that
    // reads the cost, and never dismissible.
    if (settings.blockedReason === 'budget_zero') {
      return { level: 'error', breachIndex: BUDGET_ZERO_INDEX, usagePercent: 0, spent: '', budget: '', remaining: '' }
    }
    if (settings.monthlyBudget <= 0) return null

    const usagePercent = (summary.monthlySpend / settings.monthlyBudget) * 100
    const sorted = [...settings.thresholds].sort((a, b) => a - b)

    // Find highest breached threshold
    const breached = sorted.filter(threshold => usagePercent >= threshold)
    if (breached.length === 0) return null

    const highestBreach = breached[breached.length - 1]!
    const breachIndex = Math.min(sorted.indexOf(highestBreach), 2)

    // Role visibility rules
    const isOwnerOrAdmin = isOwner() || isAdmin()
    const isManagerOrAuditor = isManager() || isAuditor()

    if (breachIndex === 0 && !isOwnerOrAdmin) return null
    if (breachIndex === 1 && !isOwnerOrAdmin && !isManagerOrAuditor) return null
    // breachIndex 2 (critical) is visible to everyone

    const currency = getCurrencyPreference()
    const levels: AlertLevel[] = ['info', 'warning', 'error']
    const remainingUSD = Math.max(0, settings.monthlyBudget - summary.monthlySpend)

    return {
      level: levels[breachIndex] ?? 'error',
      breachIndex,
      usagePercent: Math.round(usagePercent),
      spent: formatCurrency(summary.monthlySpend, currency),
      budget: formatCurrency(settings.monthlyBudget, currency),
      remaining: formatCurrency(remainingUSD, currency),
    }
  }, [isOwner, isAdmin, isManager, isAuditor])

  const fetchAndCompute = useCallback(async () => {
    try {
      const [settings, summary] = await Promise.all([
        settingsService.getCostNotificationSettings(),
        settingsService.getCostSummary(),
      ])
      const currentOrgId = settings.organizationId ?? ''
      setOrgId(currentOrgId)
      const result = computeBanner(settings, summary)
      setBanner(result)

      // Check if current breach level is dismissed (org-scoped)
      if (result && isDismissed(currentOrgId, result.breachIndex)) {
        setDismissed(true)
      } else {
        setDismissed(false)
      }
    } catch {
      // Silently fail — banner is non-critical
    }
  }, [computeBanner])

  useEffect(() => {
    if (!mayReadCost) return undefined
    fetchAndCompute()
    const interval = setInterval(fetchAndCompute, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchAndCompute, mayReadCost])

  if (!banner || dismissed) return null

  const titleKeys: Record<number, string> = {
    0: 'pages:settings.cost.bannerInfoTitle',
    1: 'pages:settings.cost.bannerWarningTitle',
    2: 'pages:settings.cost.bannerCriticalTitle',
    [BUDGET_ZERO_INDEX]: 'pages:settings.cost.bannerBudgetZeroTitle',
  }

  const messageKeys: Record<number, string> = {
    0: 'pages:settings.cost.bannerInfo',
    1: 'pages:settings.cost.bannerWarning',
    2: 'pages:settings.cost.bannerCritical',
    [BUDGET_ZERO_INDEX]: 'pages:settings.cost.bannerBudgetZero',
  }

  const titleKey = titleKeys[banner.breachIndex] ?? 'pages:settings.cost.bannerCriticalTitle'
  const messageKey = messageKeys[banner.breachIndex] ?? 'pages:settings.cost.bannerCritical'

  const handleDismiss = banner.breachIndex < 2
    ? () => {
        dismissAlert(orgId, banner.breachIndex)
        setDismissed(true)
      }
    : undefined

  return (
    <div className="px-6 pt-4">
      <Alert variant={banner.level} onDismiss={handleDismiss}>
        <AlertTitle>{t(titleKey)}</AlertTitle>
        <AlertDescription>
          {banner.breachIndex === BUDGET_ZERO_INDEX
            ? t(messageKey)
            : t(messageKey, {
              percent: banner.usagePercent,
              spent: banner.spent,
              budget: banner.budget,
              remaining: banner.remaining,
            })}
        </AlertDescription>
      </Alert>
    </div>
  )
}
